import dayjs from 'dayjs';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface Report {
  report_type: string;
  report_name: string;
  report_data: any[];
}

interface DraftReport extends Report {
  id: string;
  last_modified: string;
  is_draft: boolean;
  auto_saved: boolean;
}

interface ReportsState {
  drafts: DraftReport[];
  getDrafts: () => DraftReport[];
  getDraftById: (draftId: string) => DraftReport | undefined;
  saveDraft: (data: Partial<DraftReport>) => void;
  deleteDraft: (draftId: string) => void;
}

const useReportsStore = create<ReportsState>()(
  persist(
    (set, get) => ({
      drafts: [],
      getDrafts: () => {
        return get().drafts;
      },
      getDraftById: (draftId: string) => {
        return get().drafts.find((d) => d.id === draftId);
      },
      saveDraft: (data: Partial<DraftReport>) => {
        const drafts = get().drafts;
        const existingDraft = data.id
          ? drafts.find((d) => d.id === data.id)
          : null;

        if (existingDraft) {
          const updatedDrafts = drafts.map((d) =>
            d.id === data.id
              ? {
                  ...d,
                  ...data,
                  last_modified: dayjs().toISOString(),
                  is_draft: true,
                }
              : d
          );
          set({ drafts: updatedDrafts });
        } else {
          if (!data.report_type || !data.report_name || !data.report_data) {
            throw new Error('Missing required fields for new draft');
          }

          const newDraft: DraftReport = {
            report_type: data.report_type,
            report_name: data.report_name,
            report_data: data.report_data,
            id: crypto.randomUUID(),
            last_modified: new Date().toISOString(),
            is_draft: true,
            auto_saved: data.auto_saved || false,
          };
          set({ drafts: [...drafts, newDraft] });
        }
      },
      deleteDraft: (draftId: string) => {
        const drafts = get().drafts;
        const draft = drafts.find((d) => d.id === draftId);
        if (!draft) throw new Error('Draft not found');
        set({ drafts: drafts.filter((d) => d.id !== draftId) });
      },
    }),
    {
      name: 'reports-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        drafts: state.drafts,
      }),
    }
  )
);

export { useReportsStore };
export type { DraftReport, Report };
